import React from "react";
import {
  Box,
  Card,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  TrendingUp,
  Person,
  Phone,
  Event,
  AccessTime,
  LocationOn,
  Groups,
  CalendarMonth,
  Flatware,
} from "@mui/icons-material";

import { StatusRenderer } from "../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { useNavigate } from "react-router-dom";
import { useDispatchWrapper } from "../../../../hooks";
import { upsertUpsellLeadsAsync } from "../../../../redux/upsellLead/upsellLeadsActions";
import { IUpsertUpsellLeadPayload } from "../../../../redux/upsellLead/upsellLeads.types";
import { useBillingCompanyContext } from "../../../../providers";
import { ReadMoreText } from "../../../marketingCampaign/components/ReadMoreText";
import {
  IFoodLead,
  IServiceCategoryLead,
  IVenueLead,
} from "../../types/upsell.types";
import { getBookingPeriodLabel } from "../../constants";
import { useTimezoneFormatter } from "../../../../hooks/useTimezoneFormatter";

interface IUpsellServiceCardsProps {
  leadsData: any[];
  referenceType?: "SERVICE" | "VENUE" | "FOOD_ITEM";
  referenceUuid?: string;
  selectedBookingPeriod?: number;
}

export const UpsellServiceCards: React.FC<IUpsellServiceCardsProps> = ({
  leadsData,
  referenceType = "SERVICE",
  referenceUuid = "",
  selectedBookingPeriod,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatchWrapper();
  const { ftDate } = useTimezoneFormatter();
  const company = useBillingCompanyContext();

  // Helper to get reference UUID from lead's upselling_services array or upselling_venue object
  const getReferenceUuidValue = (
    lead: IServiceCategoryLead | IVenueLead | any,
  ) => {
    if (referenceType === "SERVICE") {
      // upselling_services is an array of service items
      const upsellingServices = (lead as IServiceCategoryLead)
        .upselling_services;
      if (Array.isArray(upsellingServices) && upsellingServices.length > 0) {
        return upsellingServices[0]?.service_item_uuid || "";
      }
      return "";
    } else if (referenceType === "VENUE") {
      // upselling_venue is a single venue object
      return (lead as IVenueLead).upselling_venue?.venue_uuid || "";
    } else if (referenceType === "FOOD_ITEM") {
      // upselling_food_item is a single food object

      const upsellingFood = (lead as IFoodLead).upselling_food_item;
      if (Array.isArray(upsellingFood) && upsellingFood.length > 0) {
        return upsellingFood[0]?.food_item_uuid || "";
      }
      return "";
    }
  };

  // Helper to get upsell service/venue name from lead
  const getUpsellServiceVenueName = (
    lead: IServiceCategoryLead | IVenueLead | any,
  ) => {
    if (referenceType === "SERVICE") {
      // upselling_services is an array of service items
      const upsellingServices = (lead as IServiceCategoryLead)
        .upselling_services;
      if (Array.isArray(upsellingServices) && upsellingServices.length > 0) {
        return upsellingServices[0]?.service_item_name || "";
      }
      return "";
    } else if (referenceType === "VENUE") {
      // upselling_venue is a single venue object
      return (lead as IVenueLead).upselling_venue?.venue_name || "";
    } else if (referenceType === "FOOD_ITEM") {
      // upselling_food_item is a single food object

      const upsellingFood = (lead as IFoodLead).upselling_food_item;
      if (Array.isArray(upsellingFood) && upsellingFood.length > 0) {
        return upsellingFood[0]?.item_name || "";
      }
      return "";
    }
  };

  const handleCreateUpsellLead = (lead: any) => {
    const upsellOpportunityDurationCategory = selectedBookingPeriod
      ? getBookingPeriodLabel(selectedBookingPeriod)
      : "";

    const payload: IUpsertUpsellLeadPayload = {
      upsell_leads_uuid: null,
      leads_uuid: lead.leads_uuid,
      reference_uuid_column_name: referenceType,
      reference_uuid_column_value: getReferenceUuidValue(lead) || "",
      reason_for_upsell: lead.reason_for_upsell,
      followup_date: null,
      upsell_opportunity_duration_category: upsellOpportunityDurationCategory,
      description: "",
      quantity: 0,
      company_outlet_uuid: company?.comapny?.company_outlet_uuid || "",
      status: "ONGOING",
    };

    dispatch(
      upsertUpsellLeadsAsync([payload], (isSuccess, responseData) => {
        if (isSuccess) {
          const newUpsellLeadUuid = responseData?.data?.[0]?.upsell_leads_uuid;
          navigate(`/upsell-leads`, {
            state: { openEditPanelForUuid: newUpsellLeadUuid },
          });
        }
      }),
    );
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {leadsData.map((lead, index) => (
          <Card
            key={lead.leads_uuid || index}
            sx={{
              width: "360px",
              height: "460px",
              display: "flex",
              flexDirection: "column",
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: `0 4px 20px ${alpha(theme.palette.grey[500], 0.12)}`,
              border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                boxShadow: `0 12px 32px ${alpha(
                  theme.palette.primary.main,
                  0.18,
                )}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            }}
          >
            {/* Card Header - Primary Color (Fixed) */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main} 100%)`,
                p: 2,
                borderRadius: 2,
                position: "relative",
                flexShrink: 0,
              }}
            >
              {/* Status Badge */}
              <Box
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                }}
              >
                <StatusRenderer status={lead.derived_status || lead.status} />
              </Box>

              {/* Guest Info */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  pr: 6,
                }}
              >
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    color: "white",
                    fontSize: "1rem",
                    fontWeight: 700,
                    backdropFilter: "blur(8px)",
                  }}
                >
                  {lead.name_of_guest
                    ?.split(" ")
                    .map((n: string) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2) || <Person />}
                </Avatar>
                <Box sx={{ overflow: "hidden" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "white",
                      fontWeight: 700,
                      fontSize: "1rem",
                      lineHeight: 1.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {lead.name_of_guest || "Unknown Guest"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontSize: "0.75rem",
                    }}
                  >
                    {lead.lead_code || ""}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Card Body - Scrollable */}
            <Box
              sx={{
                p: 2,
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                overflowY: "auto",
                minHeight: 0,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: alpha(theme.palette.grey[200], 0.5),
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: alpha(theme.palette.grey[400], 0.8),
                  borderRadius: "3px",
                  "&:hover": {
                    background: alpha(theme.palette.grey[500], 0.8),
                  },
                },
              }}
            >
              {/* Upsell Service/Venue Chip */}
              {getUpsellServiceVenueName(lead) && (
                <Box>
                  <Chip
                    icon={
                      referenceType === "SERVICE" ? (
                        <TrendingUp sx={{ fontSize: 16 }} />
                      ) : referenceType === "VENUE" ? (
                        <LocationOn sx={{ fontSize: 16 }} />
                      ) : (
                        <Flatware sx={{ fontSize: 16 }} />
                      )
                    }
                    label={getUpsellServiceVenueName(lead)}
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.dark,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      border: `1px solid ${alpha(
                        theme.palette.success.main,
                        0.3,
                      )}`,
                      "& .MuiChip-icon": {
                        color: theme.palette.success.main,
                      },
                    }}
                  />
                </Box>
              )}

              {/* Reason for Upsell */}
              {lead.reason_for_upsell && (
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.warning.main, 0.08),
                    border: `1px solid ${alpha(
                      theme.palette.warning.main,
                      0.2,
                    )}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.warning.dark,
                      fontWeight: 600,
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Reason for Upsell
                  </Typography>
                  <ReadMoreText
                    text={lead.reason_for_upsell}
                    maxLines={2}
                    variant="body2"
                  />
                </Box>
              )}

              {/* Info Grid */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                }}
              >
                {/* Event Type */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Event
                    sx={{ fontSize: 16, color: theme.palette.primary.main }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                  >
                    {lead.event_type || "--"}
                  </Typography>
                </Box>

                {/* Number of Guests */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Groups
                    sx={{ fontSize: 16, color: theme.palette.primary.main }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                  >
                    {lead.number_of_guests || 0} guests
                  </Typography>
                </Box>

                {/* Contact */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Phone
                    sx={{ fontSize: 16, color: theme.palette.primary.main }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {lead.contact_number_one || "--"}
                  </Typography>
                </Box>

                {/* Event Date */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <CalendarMonth
                    sx={{ fontSize: 16, color: theme.palette.primary.main }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                  >
                    {ftDate(lead.function_date) || "--"}
                  </Typography>
                </Box>
              </Box>

              {/* Additional Details */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.75,
                  mt: "auto",
                }}
              >
                {/* Venue */}
                {lead.preferred_venue && (
                  <Chip
                    icon={<LocationOn sx={{ fontSize: 14 }} />}
                    label={lead.preferred_venue}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: "0.7rem",
                      height: 24,
                      "& .MuiChip-icon": {
                        fontSize: 14,
                      },
                    }}
                  />
                )}

                {/* Event Time */}
                {lead.event_time && (
                  <Chip
                    icon={<AccessTime sx={{ fontSize: 14 }} />}
                    label={lead.event_time}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: "0.7rem",
                      height: 24,
                      "& .MuiChip-icon": {
                        fontSize: 14,
                      },
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Card Footer - Action Button (Fixed) */}
            <Box
              sx={{
                p: 1.5,
                pt: 1,
                display: "flex",
                justifyContent: "flex-end",
                borderTop: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
                flexShrink: 0,
              }}
            >
              <Tooltip title="Create Upsell Lead">
                <IconButton
                  onClick={() => handleCreateUpsellLead(lead)}
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <TrendingUp sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Card>
        ))}
      </Box>

      {/* Empty State */}
      {leadsData.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            color: "text.secondary",
          }}
        >
          <TrendingUp sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            No leads found
          </Typography>
          <Typography variant="body2">
            There are no upsell leads available for this selection.
          </Typography>
        </Box>
      )}
    </>
  );
};

export default UpsellServiceCards;
